const STORAGE_KEY = 'airies_awin_click_v1'
const TTL_MS = 30 * 24 * 60 * 60 * 1000

type AwinStoragePayload = {
  awc: string
  capturedAt: number
}

function debugLog(message: string, meta?: Record<string, unknown>) {
  if (!import.meta.env.DEV) return
  if (meta) {
    console.debug(`[AWIN] ${message}`, meta)
    return
  }
  console.debug(`[AWIN] ${message}`)
}

function hasWindow(): boolean {
  return typeof window !== 'undefined'
}

function normalizeAwinValue(value: string | null): string | null {
  const normalized = value?.trim()
  return normalized ? normalized : null
}

function removeFromStorage(storage: Storage) {
  try {
    storage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage failures to avoid breaking checkout flow.
  }
}

function clearStoredAwin() {
  if (!hasWindow()) return
  removeFromStorage(window.localStorage)
  removeFromStorage(window.sessionStorage)
}

function persistPayload(payload: AwinStoragePayload) {
  if (!hasWindow()) return

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore localStorage failures and still try sessionStorage.
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore sessionStorage failures to keep flow resilient.
  }
}

function readPayload(storage: Storage): AwinStoragePayload | null {
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<AwinStoragePayload>
    const awc = normalizeAwinValue(typeof parsed.awc === 'string' ? parsed.awc : null)
    const capturedAt = typeof parsed.capturedAt === 'number' ? parsed.capturedAt : NaN

    if (!awc || !Number.isFinite(capturedAt) || capturedAt <= 0) {
      return null
    }

    return { awc, capturedAt }
  } catch {
    return null
  }
}

function hasStoredValue(storage: Storage): boolean {
  try {
    return storage.getItem(STORAGE_KEY) !== null
  } catch {
    return false
  }
}

function isExpired(payload: AwinStoragePayload): boolean {
  return Date.now() - payload.capturedAt > TTL_MS
}

export function clearExpiredAwin(): void {
  if (!hasWindow()) return

  const localPayload = readPayload(window.localStorage)
  const sessionPayload = readPayload(window.sessionStorage)
  const hasExpiredPayload =
    (localPayload !== null && isExpired(localPayload)) ||
    (sessionPayload !== null && isExpired(sessionPayload)) ||
    (hasStoredValue(window.localStorage) && localPayload === null) ||
    (hasStoredValue(window.sessionStorage) && sessionPayload === null)

  if (hasExpiredPayload) {
    clearStoredAwin()
    debugLog('Cleared expired or invalid stored awc')
  }
}

export function captureAwinFromLocation(): void {
  if (!hasWindow()) return

  clearExpiredAwin()

  try {
    const params = new URLSearchParams(window.location.search)
    const awc = normalizeAwinValue(params.get('awc'))
    if (!awc) return

    const payload: AwinStoragePayload = {
      awc,
      capturedAt: Date.now(),
    }

    persistPayload(payload)
    debugLog('Captured awc from location', { awc })
  } catch {
    debugLog('Failed to capture awc from location')
  }
}

export function getStoredAwin(): string | null {
  if (!hasWindow()) return null

  clearExpiredAwin()

  const localPayload = readPayload(window.localStorage)
  if (localPayload && !isExpired(localPayload)) {
    return localPayload.awc
  }

  const sessionPayload = readPayload(window.sessionStorage)
  if (sessionPayload && !isExpired(sessionPayload)) {
    return sessionPayload.awc
  }

  return null
}

export function appendAwinToCheckoutUrl(url: string): string {
  const awc = getStoredAwin()
  if (!awc) return url

  try {
    const parsedUrl = new URL(url)
    parsedUrl.searchParams.set('x-awin', awc)
    const finalUrl = parsedUrl.toString()
    debugLog('Appended x-awin to checkout URL', { url: finalUrl })
    return finalUrl
  } catch {
    debugLog('Failed to parse checkout URL, returning original', { url })
    return url
  }
}
